import { Paper, TableBody, TableContainer, TableHead, TableRow, Theme, useMediaQuery } from "@mui/material";
import TextCell from "../components/TextCell";
import { ISliderInterface } from "../components/SliderCell";
import { DefaultButton, NormalTableRow, SettingTable, SmallContentTableRow, SmallTextTableRow, TableCellButton } from "../Settings";
import SliderRow from "../components/SliderRow";
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog";
import { useState } from "react";
import { useSnackbar } from "notistack";
import DoubleInputCell from "../components/DoubleInputCell";
import SwitchCell from "../components/SwitchCell";
import { ISelectValue } from "../components/SelectCell";
import SelectRow from "../components/SelectRow";

interface ICapturing {
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    resetValueToDefault: (name: string, basic?: boolean) => void,
    setOption: (key: string, value: string | number | boolean, basic?: boolean) => void
    handleQualityImageSetting: (w: number, h: number) => void,
    findFVStringSetting: (name: string) => string,
    qualityImageSize: [number, number, number],
    timeout: number | undefined,
    icaoWarningFilter: string,
    backgroundRemoval: boolean
}

const Capturing: React.FC<ICapturing> = (props) => {

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const { enqueueSnackbar } = useSnackbar();

    const setBackgroundRemoval = (bool: boolean) => {
        props.setOption("IcaoRemoveBackground", bool)
    }

    const setIcaoSelectedImageType = (string: string) => {
        props.setOption("IcaoSelectedImageType", string)
    }

    const handleIcaoSelectedImageTypeEnum = (): ISelectValue[] => {
        return [{key: "Best", value: "Best"}, {key: "Last", value: "Last"}]
    }

    const handleResetClick = () => {
        enqueueSnackbar("Capturing settings reset", {variant: "info"})
        props.resetValueToDefault("timeout", true)
        props.resetValueToDefault("QualityThreshold")
        props.resetValueToDefault("FramesToSkip")
        props.resetValueToDefault("StrictQualityTimeout")
        props.handleQualityImageSetting(960, 540)
        props.resetValueToDefault("IcaoRemoveBackground")
        props.resetValueToDefault("IcaoBackgroundColor")
    }

    const doubleInputRow = () => {
        if (isMobile) {
            return (
                <>
                <SmallTextTableRow>
                    <TextCell colspan={3} title="Strict Quality Image size" subtitle="Minimal frame pixel count to start detection. E.g. 480x640 would be 307200." />
                </SmallTextTableRow>
                <SmallContentTableRow>
                    <DoubleInputCell
                        qualityImageSize={props.qualityImageSize}
                        title="Image size"
                        active
                        colSpan={3}
                        saveSetting={props.handleQualityImageSetting}
                    />
                </SmallContentTableRow>
                </>
            )
        } else {
            return (
                <NormalTableRow>
                    <TextCell title="Strict Quality Image size" subtitle="Minimal frame pixel count to start detection. E.g. 480x640 would be 307200." />
                    <DoubleInputCell
                        qualityImageSize={props.qualityImageSize}
                        title="Image size"
                        active
                        colSpan={3}
                        saveSetting={props.handleQualityImageSetting}
                    />
                </NormalTableRow>
            )
        }
    }

    return (
        <>
        <TableContainer component={Paper} style={{marginBottom: "2rem", tableLayout: "fixed"}}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell head title="Capturing" subtitle=""/>
                        <TableCellButton align="right" colSpan={isMobile? 2 : 3}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Timeout"
                        subtitle="Determines operation timeout in milliseconds."
                        title="Time value"
                        option={{key: "timeout", value: props.timeout? props.timeout : 0}}
                        defaultOption={{key: "timeout", value: 0}}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={120000}
                        type="static"
                        active
                        lockMaxValue
                        steps={1000}
                        basic
                        badValueRange={[0, 1000]}
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Quality"
                        subtitle="Face quality threshold."
                        title="Threshold range"
                        option={props.findFVSliderInterface("QualityThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("QualityThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active
                        lockMaxValue
                        noWarningMsg="Everything will pass"
                        alwaysWarningMsg="Nothing will pass"
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Frames to skip"
                        subtitle="Skips first X frames before starting detection."
                        title="Frames"
                        option={props.findFVSliderInterface("FramesToSkip")}
                        defaultOption={props.findDefaultFVSliderInterface("FramesToSkip")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={1000}
                        type="static"
                        active
                        lockMaxValue
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Strict Quality Timeout"
                        subtitle="Indicates time for the image to reach timeout."
                        title="Timeout in ms"
                        option={props.findFVSliderInterface("StrictQualityTimeout")}
                        defaultOption={props.findDefaultFVSliderInterface("StrictQualityTimeout")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={60000}
                        type="static"
                        active
                        lockMaxValue
                        steps={1000}
                    />
                    {doubleInputRow()}
                    <SelectRow
                        title="Selected image type"
                        subtitle="Sets mode to regulate what type of image will be returned during successful capture event."
                        label="Mode"
                        colSpan={isMobile ? 1 : 2}
                        value={props.findFVStringSetting("IcaoSelectedImageType")}
                        isMobile={isMobile}
                        setValue={setIcaoSelectedImageType}
                        getEnumValuesAndKey={handleIcaoSelectedImageTypeEnum}
                        active
                    />
                    <TableRow>
                        <TextCell title="Remove background" subtitle="Enables or disables ICAO token image background removal"/>
                        <SwitchCell colSpan={isMobile? 2 : 3} state={props.backgroundRemoval} setState={setBackgroundRemoval}/>
                    </TableRow>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Background color"
                        subtitle="Background color of removed background if ICAO token image background removal is enabled."
                        title="Color"
                        option={props.findFVSliderInterface("IcaoBackgroundColor")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoBackgroundColor")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={255}
                        type="raisingMono"
                        active={props.backgroundRemoval}
                        lockMaxValue
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset general ICAO settings"
            text="Reset general ICAO settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    );
}

export default Capturing;